/**
 * PWA Install Prompt Component
 * Version: 6.2.0
 */
import { useState, useEffect } from 'react'
import './PWAInstallPrompt.css'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showPrompt, setShowPrompt] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)

  useEffect(() => {
    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    const wasInstalled = localStorage.getItem('pwa_installed')
    
    if (isStandalone || wasInstalled) {
      setIsInstalled(true)
      return
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      
      // Show prompt after 10 seconds (not immediately)
      setTimeout(() => {
        setShowPrompt(true)
      }, 10000)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      setDeferredPrompt(null)
      setShowPrompt(false)
      setIsInstalled(true)
      localStorage.setItem('pwa_installed', 'true')
    })

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    }
  }, [])

  const handleInstall = async () => {
    if (!deferredPrompt) return

    // Show install prompt
    await deferredPrompt.prompt()

    // Wait for user choice
    const { outcome } = await deferredPrompt.userChoice
    
    if (outcome === 'accepted') {
      localStorage.setItem('pwa_installed', 'true')
      setIsInstalled(true)
    }
    
    setDeferredPrompt(null)
    setShowPrompt(false)
  }

  const handleDismiss = () => {
    setShowPrompt(false)
    // Remember dismissal for 3 days
    localStorage.setItem('pwa_dismissed', Date.now().toString())
  }

  // Check if dismissed recently (3 days)
  useEffect(() => {
    const dismissed = localStorage.getItem('pwa_dismissed')
    if (dismissed && Date.now() - parseInt(dismissed) < 3 * 24 * 60 * 60 * 1000) {
      setShowPrompt(false)
    }
  }, [])

  if (isInstalled || !showPrompt || !deferredPrompt) {
    return null
  }

  return (
    <div className="pwa-install-prompt">
      <div className="pwa-prompt-content">
        <div className="pwa-icon">🚀</div>
        <div className="pwa-info">
          <h4>Install ZO Bull</h4>
          <p>Add to home screen for quick access</p>
        </div>
        <div className="pwa-actions">
          <button className="btn-install" onClick={handleInstall}>
            Install
          </button>
          <button className="btn-dismiss" onClick={handleDismiss}>
            ✕
          </button>
        </div>
      </div>
    </div>
  )
}

// PWA Update Notification
function PWAUpdateNotification() {
  const [show, setShow] = useState(false)

  useEffect(() => {
    // Check for updates periodically
    const checkForUpdates = () => {
      navigator.serviceWorker?.ready.then((registration) => {
        if (registration.active) {
          // Check if there's an update waiting
          registration.addEventListener('updatefound', () => {
            setShow(true)
          })
        }
      })
    }

    checkForUpdates()
  }, [])

  const handleUpdate = () => {
    navigator.serviceWorker?.controller?.postMessage('skipWaiting')
    window.location.reload()
  }

  if (!show) return null

  return (
    <div className="pwa-update-notification">
      <div className="update-content">
        <span className="update-icon">🔄</span>
        <span className="update-text">A new version is available!</span>
        <button className="btn-update" onClick={handleUpdate}>
          Update
        </button>
      </div>
    </div>
  )
}

// Offline Indicator
function OfflineIndicator() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine)

  useEffect(() => {
    const handleOffline = () => setIsOffline(true)
    const handleOnline = () => setIsOffline(false)

    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)

    return () => {
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('online', handleOnline)
    }
  }, [])

  if (!isOffline) return null

  return (
    <div className="offline-indicator">
      <span className="offline-icon">📴</span>
      <span>You're offline. Some features may be limited.</span>
    </div>
  )
}

export { PWAInstallPrompt, PWAUpdateNotification, OfflineIndicator }